import { Dupla, GameRules, GameState, HistoricoRodada } from "../types";
import { getTeamPlayersArray, createTeamFromPlayers, formatTeamName } from "./teamUtils";

export interface ComputeRachaoTransitionParams {
  gameState: GameState;
  duplas: Dupla[];
  vencedoraId: string;
  jogadorTransferido?: string;
  scoreTeam1?: number;
  scoreTeam2?: number;
  totalRodadasAnteriores?: number;
}

export interface ComputeRachaoTransitionResult {
  novoGameState: GameState;
  novasDuplas: Dupla[];
  novoHistoricoItem: HistoricoRodada;
  equipesSalvar: Dupla[];
}

/**
 * Motor determinístico de progressão e rotação de partidas do Rachão.
 * Garante que todas as invariantes estruturais sejam estritamente respeitadas:
 * 1. Nenhuma equipe é duplicada na fila, quadra ou primeira próxima.
 * 2. Nenhuma equipe ou jogador desaparece.
 * 3. Equipes incompletas são preenchidas ordenadamente sem serem promovidas desacompanhadas.
 * 4. A equipe perdedora vai para o final da fila de espera.
 * 5. A equipe completada preserva sua posição de rotação na fila.
 */
export function computeNextRachaoState(
  params: ComputeRachaoTransitionParams
): ComputeRachaoTransitionResult | null {
  const { gameState, duplas, vencedoraId, jogadorTransferido, scoreTeam1, scoreTeam2 } = params;

  if (!gameState.jogoAtual) return null;

  const [d1Id, d2Id] = gameState.jogoAtual;
  const d1 = duplas.find((d) => d.id === d1Id);
  const d2 = duplas.find((d) => d.id === d2Id);
  if (!d1 || !d2) return null;

  const regras: GameRules = gameState.regras || {
    tamanhoEquipe: 2,
    modoPlay: "solo",
    limiteVitorias: 0,
    destinoVencedorLimite: "primeira_proxima",
  };
  const size = regras.tamanhoEquipe || 2;

  const vencedora = d1Id === vencedoraId ? d1 : d2;
  const perdedora = d1Id === vencedoraId ? d2 : d1;

  // Snapshot exato e imutável dos jogadores que participaram desta rodada
  const jogadoresD1 = getTeamPlayersArray(d1);
  const jogadoresD2 = getTeamPlayersArray(d2);
  const jogadoresVencedores = d1Id === vencedoraId ? [...jogadoresD1] : [...jogadoresD2];
  const jogadoresPerdedores = d1Id === vencedoraId ? [...jogadoresD2] : [...jogadoresD1];

  let novasDuplas = [...duplas];
  const equipesSalvar: Dupla[] = [];
  let jogadorRestante = "";
  let itemJogadorTransferido = "";
  let novaDuplaCriada: Dupla | null = null;

  let transferidos = (jogadorTransferido || "")
    .split(",")
    .map((p) => p.trim())
    .filter(Boolean);

  // 1. PROCESSAR TRANSFERÊNCIA DE JOGADOR
  // Ordem de prioridade estrita para localizar equipe incompleta:
  // 1º: Equipe em primeiraProxima (se incompleta)
  // 2º: Equipes na fila em ordem (gameState.fila)
  // 3º: Qualquer outra equipe em novasDuplas (fora de quadra)
  const candidatosIncompletos: Dupla[] = [];

  if (gameState.primeiraProxima && gameState.primeiraProxima !== vencedora.id && gameState.primeiraProxima !== perdedora.id) {
    const pTeam = novasDuplas.find((d) => d.id === gameState.primeiraProxima);
    if (pTeam) candidatosIncompletos.push(pTeam);
  }

  (gameState.fila || []).forEach((fId) => {
    if (fId !== vencedora.id && fId !== perdedora.id && fId !== gameState.primeiraProxima) {
      const fTeam = novasDuplas.find((d) => d.id === fId);
      if (fTeam) candidatosIncompletos.push(fTeam);
    }
  });

  novasDuplas.forEach((d) => {
    if (d.id !== vencedora.id && d.id !== perdedora.id && !candidatosIncompletos.some((c) => c.id === d.id)) {
      candidatosIncompletos.push(d);
    }
  });

  const targetTeam = candidatosIncompletos.find((d) => {
    const pCount = getTeamPlayersArray(d).length;
    return pCount > 0 && pCount < size;
  });

  // Se em modo solo há equipe incompleta, garantir seleção válida dos jogadores perdedores
  if (regras.modoPlay === "solo") {
    const playersPerdedores = getTeamPlayersArray(perdedora);
    const validPassedTransferidos = transferidos.filter((p) => playersPerdedores.includes(p));

    if (validPassedTransferidos.length > 0) {
      transferidos = validPassedTransferidos;
    } else {
      // Auto-selecionar jogadores perdedores necessários para completar a equipe incompleta
      if (targetTeam) {
        const targetCount = getTeamPlayersArray(targetTeam).length;
        const needCount = Math.min(size - targetCount, Math.max(1, playersPerdedores.length - 1));
        if (playersPerdedores.length > 1 && needCount > 0) {
          transferidos = playersPerdedores.slice(playersPerdedores.length - needCount);
        }
      } else {
        const playersIncompletos = (gameState.equipeIncompleta || "")
          .split(",")
          .map((p) => p.trim())
          .filter(Boolean);
        if (playersIncompletos.length > 0 && playersIncompletos.length < size) {
          const needCount = Math.min(size - playersIncompletos.length, Math.max(1, playersPerdedores.length - 1));
          if (playersPerdedores.length > 1 && needCount > 0) {
            transferidos = playersPerdedores.slice(playersPerdedores.length - needCount);
          }
        }
      }
    }
  }

  if (transferidos.length > 0) {
    const playersPerdedores = getTeamPlayersArray(perdedora);
    const remanescentes = playersPerdedores.filter((p) => !transferidos.includes(p));
    jogadorRestante = remanescentes.join(", ");
    itemJogadorTransferido = transferidos.join(", ");

    if (targetTeam) {
      // Caso 1: Já existe uma equipe incompleta na rotação (ex: Time 7 com 1 jogador)
      const currentTargetPlayers = getTeamPlayersArray(targetTeam);
      const mergedPlayers = Array.from(new Set([...currentTargetPlayers, ...transferidos]));

      const updatedTargetTeam = createTeamFromPlayers(
        targetTeam.id,
        targetTeam.name,
        mergedPlayers,
        targetTeam.isAutoCreated
      );

      const updatedPerdedora = createTeamFromPlayers(
        perdedora.id,
        perdedora.name,
        remanescentes,
        perdedora.isAutoCreated
      );

      novasDuplas = novasDuplas.map((d) => {
        if (d.id === targetTeam.id) return updatedTargetTeam;
        if (d.id === perdedora.id) return updatedPerdedora;
        return d;
      });

      equipesSalvar.push(updatedTargetTeam, updatedPerdedora);
    } else {
      // Caso 2: Não há equipe incompleta pré-criada, verificar atletas solos em equipeIncompleta
      const playersIncompletos = (gameState.equipeIncompleta || "")
        .split(",")
        .map((p) => p.trim())
        .filter(Boolean);

      if (playersIncompletos.length > 0) {
        const novosPlayersDaEquipe = [...playersIncompletos, ...transferidos];
        const nextTeamNum = novasDuplas.length + 1;
        const novaDuplaId = `dupla_auto_${Date.now()}_${Math.random().toString(36).substring(2, 7)}`;
        const novaDuplaName = formatTeamName(nextTeamNum);

        novaDuplaCriada = createTeamFromPlayers(
          novaDuplaId,
          novaDuplaName,
          novosPlayersDaEquipe,
          true
        );

        const updatedPerdedora = createTeamFromPlayers(
          perdedora.id,
          perdedora.name,
          remanescentes,
          perdedora.isAutoCreated
        );

        novasDuplas.push(novaDuplaCriada);
        novasDuplas = novasDuplas.map((d) => (d.id === perdedora.id ? updatedPerdedora : d));

        equipesSalvar.push(novaDuplaCriada, updatedPerdedora);
      } else {
        // Caso 3: Transferência direta entre as equipes em quadra
        const winnerPlayers = getTeamPlayersArray(vencedora);
        const combinedWinner = [...winnerPlayers, ...transferidos];

        const updatedVencedora = createTeamFromPlayers(
          vencedora.id,
          vencedora.name,
          combinedWinner,
          vencedora.isAutoCreated
        );

        const updatedPerdedora = createTeamFromPlayers(
          perdedora.id,
          perdedora.name,
          remanescentes,
          perdedora.isAutoCreated
        );

        novasDuplas = novasDuplas.map((d) => {
          if (d.id === vencedora.id) return updatedVencedora;
          if (d.id === perdedora.id) return updatedPerdedora;
          return d;
        });

        equipesSalvar.push(updatedVencedora, updatedPerdedora);
      }
    }
  } else {
    // Se não houve jogador transferido, manter os remanescentes conhecidos
    jogadorRestante = gameState.equipeIncompleta || "";
  }

  // 2. CÁLCULO DE VITÓRIAS CONSECUTIVAS E LIMITE DE VITÓRIAS
  let novasVitoriasConsecutivas = 1;
  if (gameState.idEquipeVencedoraAtual === vencedora.id) {
    novasVitoriasConsecutivas = (gameState.vitoriasConsecutivas || 0) + 1;
  }
  const atingiuLimite = regras.limiteVitorias > 0 && novasVitoriasConsecutivas >= regras.limiteVitorias;

  // 3. CONSTRUÇÃO DA FILA DE ESPERA (WAITLIST)
  // Inicia com os times que já estavam esperando
  let waitlist = [...(gameState.fila || [])];

  // Se uma nova equipe foi montada com atletas avulsos, ela entra na fila
  if (novaDuplaCriada && !waitlist.includes(novaDuplaCriada.id)) {
    waitlist.push(novaDuplaCriada.id);
  }

  // A equipe perdedora (que agora contém o jogador remanescente) vai para o final da fila de espera
  waitlist.push(perdedora.id);

  // Higienização inicial: deduplicação de IDs e exclusão do vencedor da waitlist
  waitlist = Array.from(new Set(waitlist)).filter((id) => Boolean(id) && id !== vencedora.id);

  let novoJogoAtual: [string, string] | null = null;
  let novaPrimeiraProxima: string | null = null;
  let novaFila: string[] = [];

  const antigaPrimeiraProxima = gameState.primeiraProxima;

  // 4. TRANSIÇÃO DE POSIÇÕES
  if (!atingiuLimite) {
    // O vencedor continua em quadra!
    let oponente: string | null = null;

    if (antigaPrimeiraProxima && antigaPrimeiraProxima !== vencedora.id) {
      oponente = antigaPrimeiraProxima;
      // Remove o oponente da waitlist se estiver lá
      waitlist = waitlist.filter((id) => id !== oponente);
      novaPrimeiraProxima = waitlist.shift() || null;
    } else {
      oponente = waitlist.shift() || null;
      novaPrimeiraProxima = waitlist.shift() || null;
    }

    if (vencedora.id && oponente) {
      novoJogoAtual = [vencedora.id, oponente];
    } else if (vencedora.id) {
      novoJogoAtual = [vencedora.id, perdedora.id];
      waitlist = [];
      novaPrimeiraProxima = null;
    }

    novaFila = waitlist;
  } else {
    // Vencedor atingiu o limite de vitórias! Ambas as equipes saem da quadra.
    let p1: string | null = null;
    if (antigaPrimeiraProxima && antigaPrimeiraProxima !== vencedora.id) {
      p1 = antigaPrimeiraProxima;
      waitlist = waitlist.filter((id) => id !== p1);
    } else {
      p1 = waitlist.shift() || null;
    }

    const p2 = waitlist.shift() || null;

    if (p1 && p2) {
      novoJogoAtual = [p1, p2];
    } else if (p1) {
      novoJogoAtual = [p1, vencedora.id];
    } else {
      novoJogoAtual = null;
    }

    const proximoCandidato = waitlist.shift() || null;
    const limiteDestino = regras.destinoVencedorLimite || "primeira_proxima";

    if (limiteDestino === "primeira_proxima") {
      novaPrimeiraProxima = vencedora.id;
      if (proximoCandidato) {
        waitlist.unshift(proximoCandidato);
      }
      novaFila = waitlist;
    } else if (limiteDestino === "segunda_proxima") {
      novaPrimeiraProxima = proximoCandidato;
      waitlist.unshift(vencedora.id);
      novaFila = waitlist;
    } else if (limiteDestino === "terceira_proxima") {
      novaPrimeiraProxima = proximoCandidato;
      waitlist.splice(1, 0, vencedora.id);
      novaFila = waitlist;
    } else {
      novaPrimeiraProxima = proximoCandidato;
      const waitlistSemPerdedor = waitlist.filter((id) => id !== perdedora.id);
      novaFila = [...waitlistSemPerdedor, vencedora.id, perdedora.id];
    }

    novasVitoriasConsecutivas = 0;
  }

  // 5. INVARIANTE ABSOLUTA: SANITIZAÇÃO E CONSISTÊNCIA ESTRUTURAL
  const usedIds = new Set<string>();

  // A. Quadra
  if (novoJogoAtual) {
    let [c1, c2] = novoJogoAtual;
    if (c1 === c2) {
      c2 = novaFila.shift() || "";
      novoJogoAtual = c2 ? [c1, c2] : [c1, perdedora.id];
    }
    if (novoJogoAtual[0]) usedIds.add(novoJogoAtual[0]);
    if (novoJogoAtual[1]) usedIds.add(novoJogoAtual[1]);
  }

  // B. Primeira Próxima
  if (novaPrimeiraProxima && usedIds.has(novaPrimeiraProxima)) {
    novaPrimeiraProxima = null;
  }

  // C. Fila de Espera
  novaFila = Array.from(new Set(novaFila)).filter((id) => Boolean(id) && !usedIds.has(id));
  if (novaPrimeiraProxima) {
    novaFila = novaFila.filter((id) => id !== novaPrimeiraProxima);
  }

  // Se Primeira Próxima ficou nula mas há times na fila, o 1º da fila sobe para Primeira Próxima
  if (!novaPrimeiraProxima && novaFila.length > 0) {
    novaPrimeiraProxima = novaFila.shift() || null;
  }
  if (novaPrimeiraProxima) {
    usedIds.add(novaPrimeiraProxima);
  }

  // D. Garantir que nenhuma equipe com atletas seja esquecida fora das posições
  novasDuplas.forEach((d) => {
    const pCount = getTeamPlayersArray(d).length;
    if (pCount > 0 && !usedIds.has(d.id) && !novaFila.includes(d.id)) {
      novaFila.push(d.id);
    }
  });

  // E. Sincronizar equipeIncompleta com a realidade das equipes fora de quadra
  let syncEquipeIncompleta = jogadorRestante;
  if (regras.modoPlay === "solo") {
    // Procura se existe alguma equipe fora de quadra que ainda está incompleta
    const foraDeQuadraIncompleta = [
      novaPrimeiraProxima ? novasDuplas.find((d) => d.id === novaPrimeiraProxima) : null,
      ...novaFila.map((id) => novasDuplas.find((d) => d.id === id)),
    ].find((d) => d && getTeamPlayersArray(d).length > 0 && getTeamPlayersArray(d).length < size);

    if (foraDeQuadraIncompleta) {
      syncEquipeIncompleta = getTeamPlayersArray(foraDeQuadraIncompleta).join(", ");
    }
  }

  // 6. GERAR ITEM DE HISTÓRICO DETERMINÍSTICO
  const roundNumber = (params.totalRodadasAnteriores ?? 0) + 1;
  const placarFormatado =
    scoreTeam1 !== undefined && scoreTeam2 !== undefined
      ? `${d1.name} (${scoreTeam1}) x (${scoreTeam2}) ${d2.name}`
      : undefined;

  const novoHistoricoItem: HistoricoRodada = {
    id: `round_${Date.now()}_${Math.random().toString(36).substring(2, 6)}`,
    rodada: roundNumber,
    dupla1: d1.name,
    dupla2: d2.name,
    dupla1Id: d1.id,
    dupla2Id: d2.id,
    vencedora: vencedora.name,
    perdedora: perdedora.name,
    vencedoraId: vencedora.id,
    perdedoraId: perdedora.id,
    jogadoresDupla1: [...jogadoresD1],
    jogadoresDupla2: [...jogadoresD2],
    jogadoresVencedores: [...jogadoresVencedores],
    jogadoresPerdedores: [...jogadoresPerdedores],
    jogadorTransferido: itemJogadorTransferido,
    primeiraProxima: novaPrimeiraProxima
      ? novasDuplas.find((d) => d.id === novaPrimeiraProxima)?.name || ""
      : perdedora.name,
    horario: new Date().toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" }),
    placar: placarFormatado,
    pontosVencedor: scoreTeam1 !== undefined && scoreTeam2 !== undefined
      ? (vencedora.id === d1.id ? scoreTeam1 : scoreTeam2)
      : undefined,
    pontosPerdedor: scoreTeam1 !== undefined && scoreTeam2 !== undefined
      ? (vencedora.id === d1.id ? scoreTeam2 : scoreTeam1)
      : undefined,
  };

  const novoGameState: GameState = {
    emAndamento: true,
    jogoAtual: novoJogoAtual,
    primeiraProxima: novaPrimeiraProxima,
    equipeIncompleta: syncEquipeIncompleta,
    fila: novaFila,
    vitoriasConsecutivas: atingiuLimite ? 0 : novasVitoriasConsecutivas,
    idEquipeVencedoraAtual: atingiuLimite ? null : vencedora.id,
    regras: regras,
    organizerUserId: gameState.organizerUserId,
    organizerId: gameState.organizerId,
  };

  return {
    novoGameState,
    novasDuplas,
    novoHistoricoItem,
    equipesSalvar,
  };
}
